import * as React from 'react';
import SearchField from '../components/SearchField';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useCreateActivation from '@/src/Activations/components/hooks/useCreateActivation';
import useActivations from '@/src/Activations/components/hooks/useActivations';
import PeriodFilter, {
    PeriodKey,
    periodLabel,
} from '../components/PeriodFilter';
import FilterMenu, {
    ActiveFilter,
    FilterDimension,
} from '../components/FilterMenu';

const IconLead = (
    <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <circle cx="8" cy="5.5" r="2.8" />
        <path d="M2.8 13.5a5.2 5.2 0 0 1 10.4 0" />
    </svg>
);

const IconLocation = (
    <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M13 6.7c0 3.5-5 8-5 8s-5-4.5-5-8a5 5 0 0 1 10 0Z" />
        <circle cx="8" cy="6.7" r="1.8" />
    </svg>
);

/*
 * Activations v2 — the big-table archetype on live data.
 * Design: docs/design-system/mocks/activations.html; period + filter
 * controls per the Visitors reference. Periods are forward-looking
 * (events live in the future); default is This week. Structured
 * filters: Lead and Location today — tags/status join when their
 * endpoints land.
 */

interface ActivationRow {
    id: number;
    name: string;
    date: string | Date;
    location: string;
    owner: string;
    isArchived: boolean;
}

function periodRange(
    key: PeriodKey,
): [moment.Moment, moment.Moment] | null {
    const now = moment();
    switch (key) {
        case 'today':
            return [now.clone().startOf('day'), now.clone().endOf('day')];
        case 'tomorrow':
            return [
                now.clone().add(1, 'day').startOf('day'),
                now.clone().add(1, 'day').endOf('day'),
            ];
        case 'this_week':
            return [now.clone().startOf('week'), now.clone().endOf('week')];
        case 'this_month':
            return [now.clone().startOf('month'), now.clone().endOf('month')];
        case 'this_year':
            return [now.clone().startOf('year'), now.clone().endOf('year')];
        case 'next_7':
            return [
                now.clone().startOf('day'),
                now.clone().add(6, 'days').endOf('day'),
            ];
        case 'next_30':
            return [
                now.clone().startOf('day'),
                now.clone().add(29, 'days').endOf('day'),
            ];
        case 'next_6m':
            return [
                now.clone().startOf('day'),
                now.clone().add(6, 'months').endOf('day'),
            ];
        case 'next_12m':
            return [
                now.clone().startOf('day'),
                now.clone().add(12, 'months').endOf('day'),
            ];
        case 'all_time':
            return null;
    }
}

function proximity(date: moment.Moment): string {
    const today = moment().startOf('day');
    const d = date.clone().startOf('day');
    const days = d.diff(today, 'days');
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days === -1) return 'Yesterday';
    if (days > 1 && days <= 7) return 'This week';
    if (days > 7 && days <= 21) return `In ${Math.round(days / 7)} weeks`;
    if (days > 21 && days <= 60) return 'Next month';
    if (days < -1 && days >= -7) return 'Last week';
    return d.format('YYYY');
}

export default function ActivationsV2() {
    const { getActivations } = useActivations();
    const authenticatedApi = useAuthenticatedApi();
    const { cloneActivation } = useCreateActivation();

    const rowAction = async (
        e: React.MouseEvent,
        a: any,
        action: 'archive' | 'unarchive' | 'clone',
    ) => {
        e.stopPropagation();
        if (action === 'clone') {
            const name = window.prompt(
                `Clone "${a.name}" — name for the copy:`,
                `${a.name} (copy)`,
            );
            if (!name) return;
            const created = await cloneActivation(
                a.id,
                name.trim(),
                moment(a.date).format('YYYY-MM-DD'),
                a.jobId,
            ).catch(() => null);
            if (created?.id) navigate(`/v2/activations/${created.id}`);
            return;
        }
        if (
            action === 'archive' &&
            !window.confirm(`Archive "${a.name}"?`)
        )
            return;
        await authenticatedApi
            .put(`activations/${a.id}/${action}`)
            .catch(() => null);
        setRows((rs: any[]) =>
            rs.map((r) =>
                r.id === a.id
                    ? { ...r, isArchived: action === 'archive' }
                    : r,
            ),
        );
    };
    const navigate = useNavigate();
    const [rows, setRows] = useState<ActivationRow[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [period, setPeriod] = useState<PeriodKey>('this_week');
    const [filters, setFilters] = useState<ActiveFilter[]>([]);

    useEffect(() => {
        let mounted = true;
        getActivations()
            .then((data: ActivationRow[]) => {
                if (mounted) setRows(data ?? []);
            })
            .catch(() => {
                /* surfaced via the empty state for now */
            })
            .finally(() => {
                if (mounted) setIsLoading(false);
            });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dimensions: FilterDimension[] = useMemo(() => {
        const distinct = (values: (string | undefined)[]) =>
            Array.from(new Set(values.filter(Boolean) as string[])).sort(
                (a, b) => a.localeCompare(b),
            );
        return [
            {
                key: 'lead',
                label: 'Lead',
                icon: IconLead,
                values: distinct(rows.map((r) => r.owner)),
            },
            {
                key: 'location',
                label: 'Location',
                icon: IconLocation,
                values: distinct(rows.map((r) => r.location)),
            },
        ];
    }, [rows]);

    const addFilter = (filter: ActiveFilter) => {
        setFilters((prev) => [
            ...prev.filter((f) => f.key !== filter.key),
            filter,
        ]);
    };

    const removeFilter = (key: string) => {
        setFilters((prev) => prev.filter((f) => f.key !== key));
    };

    const PAGE = 50;
    const [limit, setLimit] = useState(PAGE);
    useEffect(() => {
        setLimit(PAGE);
    }, [period, search, filters]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const searching = q.length > 0;
        /* an active search spans all time (period would hide most
           matches) and returns newest first; browsing by period keeps
           soonest-first */
        const range = searching ? null : periodRange(period);
        const result = rows.filter((a) => {
            if (!searching && a.isArchived && period !== 'all_time')
                return false;
            if (range) {
                const d = moment(a.date);
                if (d.isBefore(range[0]) || d.isAfter(range[1])) return false;
            }
            for (const f of filters) {
                if (f.key === 'lead' && a.owner !== f.value) return false;
                if (f.key === 'location' && a.location !== f.value)
                    return false;
            }
            if (q && !`${a.name} ${a.location}`.toLowerCase().includes(q))
                return false;
            return true;
        });
        result.sort((a, b) =>
            searching
                ? moment(b.date).valueOf() - moment(a.date).valueOf()
                : moment(a.date).valueOf() - moment(b.date).valueOf(),
        );
        return result;
    }, [rows, search, period, filters]);

    return (
        <main className="mx-auto max-w-[1060px] px-[clamp(18px,3vw,34px)] pt-4">
            {/* toolbar: period left · search + filter right */}
            <div className="mb-3 flex flex-wrap items-center gap-3">
                <PeriodFilter value={period} onChange={setPeriod} />
                <div className="flex-1" />
                <SearchField
                    value={search}
                    onChange={setSearch}
                    placeholder="Search activations…"
                    className="w-full max-w-[260px]"
                />
                <FilterMenu dimensions={dimensions} onAdd={addFilter} />
            </div>

            {/* active filter chips */}
            {filters.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-2">
                    {filters.map((f) => (
                        <span
                            key={f.key}
                            title={`${f.label} is ${f.value}`}
                            className="inline-flex items-center gap-1.5 rounded-full border-[1.5px] border-dashed border-border px-[11px] py-[5px] text-xs text-muted-foreground"
                        >
                            <span className="flex text-faint">
                                {dimensions.find((d) => d.key === f.key)?.icon}
                            </span>
                            is{' '}
                            <b className="font-semibold text-foreground">
                                {f.value}
                            </b>
                            <button
                                type="button"
                                aria-label={`Remove ${f.label} filter`}
                                onClick={() => removeFilter(f.key)}
                                className="cursor-pointer border-0 bg-transparent p-0 text-[13px] leading-none text-faint hover:text-foreground"
                            >
                                ×
                            </button>
                        </span>
                    ))}
                </div>
            )}

            {/* table */}
            <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[13px]">
                    <thead>
                        <tr>
                            <th className="ps-label border-b border-border pb-2 pl-2.5 pr-2.5 text-left font-semibold">
                                Date
                            </th>
                            <th className="ps-label border-b border-border pb-2 pr-2.5 text-left font-semibold">
                                Activation
                            </th>
                            <th className="ps-label border-b border-border pb-2 pr-2.5 text-left font-semibold">
                                Lead
                            </th>
                            <th className="border-b border-border" />
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading &&
                            [0, 1, 2, 3, 4].map((i) => (
                                <tr key={i}>
                                    <td colSpan={4} className="px-2.5 py-3">
                                        <div className="h-9 animate-pulse rounded-[9px] bg-secondary" />
                                    </td>
                                </tr>
                            ))}
                        {!isLoading &&
                            filtered.slice(0, limit).map((a) => {
                                const d = moment(a.date);
                                return (
                                    <tr
                                        key={a.id}
                                        onClick={() =>
                                            navigate(`/v2/activations/${a.id}`)
                                        }
                                        className="group cursor-pointer border-b border-line hover:bg-sunk"
                                    >
                                        <td className="w-[190px] py-2.5 pl-2.5 pr-2.5">
                                            <div className="flex items-center gap-2">
                                                <div className="w-11 flex-none rounded-lg border border-border py-1 text-center">
                                                    <div className="font-mono text-[8.5px] uppercase tracking-[0.06em] text-attention">
                                                        {d.format('MMM')}
                                                    </div>
                                                    <div className="text-sm font-semibold tabular-nums leading-tight">
                                                        {d.format('DD')}
                                                    </div>
                                                </div>
                                                <span className="text-[11.5px] text-faint">
                                                    {proximity(d)}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-2.5 pr-2.5">
                                            <div className="font-semibold tracking-[-0.005em]">
                                                {a.name}
                                                {a.isArchived && (
                                                    <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">
                                                        archived
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-px text-[11.5px] text-faint">
                                                {a.location || '—'}
                                            </div>
                                        </td>
                                        <td className="py-2.5 pr-2.5 text-[12.5px] text-muted-foreground">
                                            {a.owner || '—'}
                                        </td>
                                        <td className="w-[150px] whitespace-nowrap py-2.5 pr-1 text-right">
                                            <span className="hidden gap-3 group-hover:inline-flex">
                                                <button
                                                    type="button"
                                                    onClick={(e) =>
                                                        rowAction(
                                                            e,
                                                            a,
                                                            'clone',
                                                        )
                                                    }
                                                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-brand-ink"
                                                >
                                                    Clone
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={(e) =>
                                                        rowAction(
                                                            e,
                                                            a,
                                                            a.isArchived
                                                                ? 'unarchive'
                                                                : 'archive',
                                                        )
                                                    }
                                                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-faint hover:text-attention"
                                                >
                                                    {a.isArchived
                                                        ? 'Unarchive'
                                                        : 'Archive'}
                                                </button>
                                            </span>
                                            <span className="text-faint group-hover:hidden">
                                                →
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        {!isLoading && filtered.length === 0 && (
                            <tr>
                                <td
                                    colSpan={4}
                                    className="px-2.5 py-10 text-center text-[13px] text-muted-foreground"
                                >
                                    {search || filters.length > 0
                                        ? 'No activations match your filters.'
                                        : `No activations in ${periodLabel(
                                              period,
                                          ).toLowerCase()} — try another period, or press + to create one.`}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {!isLoading && (
                <div className="mt-3 flex items-center justify-between text-xs text-faint">
                    <span>
                        {Math.min(limit, filtered.length)} of{' '}
                        {filtered.length} activations ·{' '}
                        {search.trim()
                            ? 'Search · newest first'
                            : periodLabel(period)}
                    </span>
                    {filtered.length > limit && (
                        <button
                            type="button"
                            onClick={() => setLimit((l) => l + PAGE)}
                            className="ps-pill cursor-pointer px-4 font-[inherit] text-[12.5px] font-semibold text-foreground"
                        >
                            Show more
                        </button>
                    )}
                </div>
            )}
        </main>
    );
}
